// Teams of a company, shown on the company profile page (see
// `features/teams-sports.md`). Each team belongs to one sport (a company may
// span several) and lists its members: memberships linking an athlete's sport
// profile to the team, carrying the team-scoped `tier` and `teamRole`.
// Reading comes with company access; creating, editing and deleting teams and
// members are gated by MANAGE_TEAMS (the server enforces the same rules on
// every request). Every mutation responds with the company's refreshed
// { teams, memberships } snapshot, rendered directly.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import type { Athlete, Membership, Sport, SportProfile, Team } from '../domain';
import type { TeamsSnapshot } from '../data/apiClient';
import {
  deleteMembership,
  deleteTeam,
  fetchAthletes,
  fetchSportProfiles,
  fetchSports,
  fetchTeams,
} from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { TeamDialog } from './TeamDialog';
import { MembershipDialog } from './MembershipDialog';

interface TeamsSectionProps {
  companyId: number;
}

export function TeamsSection({ companyId }: TeamsSectionProps) {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const canManage = hasPermission('MANAGE_TEAMS');

  const [teams, setTeams] = useState<Team[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [profiles, setProfiles] = useState<SportProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [pendingTeamDelete, setPendingTeamDelete] = useState<Team | null>(null);
  const [memberDialogTeam, setMemberDialogTeam] = useState<Team | null>(null);
  const [editingMembership, setEditingMembership] = useState<Membership | null>(null);
  const [pendingRemove, setPendingRemove] = useState<{
    membership: Membership;
    team: Team;
  } | null>(null);

  // Read the translator through a ref so the loader's identity is stable
  // across language switches (same pattern as the athletes loader).
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [snapshot, catalog, roster, companyProfiles] = await Promise.all([
        fetchTeams(companyId),
        fetchSports(),
        fetchAthletes(companyId),
        fetchSportProfiles(companyId),
      ]);
      setTeams(snapshot.teams);
      setMemberships(snapshot.memberships);
      setSports(catalog);
      setAthletes(roster);
      setProfiles(companyProfiles);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : tRef.current('admin.teams.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const applySnapshot = (snapshot: TeamsSnapshot) => {
    setTeams(snapshot.teams);
    setMemberships(snapshot.memberships);
  };

  const sportById = useMemo(
    () => new Map(sports.map((sport) => [sport.id, sport])),
    [sports],
  );
  const profileById = useMemo(
    () => new Map(profiles.map((profile) => [profile.id, profile])),
    [profiles],
  );
  const athleteByNumber = useMemo(
    () => new Map(athletes.map((athlete) => [athlete.personalNumber, athlete])),
    [athletes],
  );
  const membersByTeam = useMemo(() => {
    const map = new Map<number, Membership[]>();
    for (const membership of memberships) {
      const list = map.get(membership.teamId) ?? [];
      list.push(membership);
      map.set(membership.teamId, list);
    }
    return map;
  }, [memberships]);

  // Prefer the locale catalogue entry for a sport name; sports missing from
  // the catalogues fall back to the server-provided English name.
  const sportName = useCallback(
    (sportId: string) => {
      const translated = t(`sports.${sportId}`);
      if (translated !== `sports.${sportId}`) return translated;
      return sportById.get(sportId)?.name ?? sportId;
    },
    [t, sportById],
  );

  const memberName = (membership: Membership) => {
    const profile = profileById.get(membership.sportProfileId);
    return profile ? (athleteByNumber.get(profile.athleteId)?.fullName ?? '') : '';
  };

  const runAction = async (action: () => Promise<TeamsSnapshot>, fallbackKey: string) => {
    setBusy(true);
    setError(null);
    try {
      applySnapshot(await action());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t(fallbackKey));
    } finally {
      setBusy(false);
    }
  };

  const confirmTeamDelete = async () => {
    if (!pendingTeamDelete) return;
    const target = pendingTeamDelete;
    setPendingTeamDelete(null);
    await runAction(() => deleteTeam(target.id), 'admin.teams.errors.delete');
  };

  const confirmRemove = async () => {
    if (!pendingRemove) return;
    const target = pendingRemove;
    setPendingRemove(null);
    await runAction(
      () => deleteMembership(target.membership.id),
      'admin.teams.errors.remove',
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('admin.teams.title')}
        </Typography>
        {canManage && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={busy || loading}
            onClick={() => {
              setEditingTeam(null);
              setTeamDialogOpen(true);
            }}
          >
            {t('admin.teams.addTeam')}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : teams.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t(canManage ? 'admin.teams.emptyEditable' : 'admin.teams.empty')}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {teams.map((team) => {
            const members = membersByTeam.get(team.id) ?? [];
            return (
              <Paper key={team.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                  <GroupsIcon color="primary" />
                  <Typography
                    variant="subtitle1"
                    noWrap
                    title={team.name}
                    sx={{ minWidth: 0 }}
                  >
                    {team.name}
                  </Typography>
                  <Chip size="small" color="info" label={sportName(team.sportId)} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexGrow: 1 }}
                  >
                    {t('admin.teams.members', { count: members.length })}
                  </Typography>
                  {canManage && (
                    <>
                      <Tooltip title={t('admin.teams.addMember')}>
                        <span>
                          <IconButton
                            aria-label={t('admin.teams.addMemberAria', {
                              name: team.name,
                            })}
                            disabled={busy}
                            onClick={() => {
                              setEditingMembership(null);
                              setMemberDialogTeam(team);
                            }}
                          >
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('admin.teams.editAria', { name: team.name })}>
                        <span>
                          <IconButton
                            aria-label={t('admin.teams.editAria', { name: team.name })}
                            disabled={busy}
                            onClick={() => {
                              setEditingTeam(team);
                              setTeamDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('admin.teams.deleteAria', { name: team.name })}>
                        <span>
                          <IconButton
                            aria-label={t('admin.teams.deleteAria', {
                              name: team.name,
                            })}
                            disabled={busy}
                            onClick={() => setPendingTeamDelete(team)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Stack>

                {members.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      canManage
                        ? 'admin.teams.noMembersEditable'
                        : 'admin.teams.noMembers',
                    )}
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {members.map((membership) => {
                      const name = memberName(membership);
                      const profile = profileById.get(membership.sportProfileId);
                      return (
                        <Stack
                          key={membership.id}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ pl: 4.5, py: 0.25 }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            title={name}
                            sx={{ minWidth: 0 }}
                          >
                            {name}
                          </Typography>
                          {profile && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={profile.athleteId}
                              sx={{ fontFamily: 'monospace' }}
                            />
                          )}
                          {membership.teamRole && (
                            <Chip
                              size="small"
                              color="secondary"
                              label={membership.teamRole}
                            />
                          )}
                          {membership.tier && (
                            <Chip size="small" variant="outlined" label={membership.tier} />
                          )}
                          <Box sx={{ flexGrow: 1 }} />
                          {canManage && (
                            <>
                              <Tooltip
                                title={t('admin.teams.memberEditAria', { name })}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={t('admin.teams.memberEditAria', {
                                      name,
                                    })}
                                    disabled={busy}
                                    onClick={() => {
                                      setEditingMembership(membership);
                                      setMemberDialogTeam(team);
                                    }}
                                  >
                                    <EditIcon fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip
                                title={t('admin.teams.memberRemoveAria', { name })}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={t('admin.teams.memberRemoveAria', {
                                      name,
                                    })}
                                    disabled={busy}
                                    onClick={() =>
                                      setPendingRemove({ membership, team })
                                    }
                                  >
                                    <PersonRemoveIcon fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      <TeamDialog
        open={teamDialogOpen}
        companyId={companyId}
        team={editingTeam}
        sports={sports}
        onClose={() => setTeamDialogOpen(false)}
        onSaved={applySnapshot}
      />

      {memberDialogTeam && (
        <MembershipDialog
          open={memberDialogTeam !== null}
          team={memberDialogTeam}
          membership={editingMembership}
          athletes={athletes}
          profiles={profiles}
          memberships={memberships}
          sport={sportById.get(memberDialogTeam.sportId)}
          onClose={() => setMemberDialogTeam(null)}
          onSaved={applySnapshot}
        />
      )}

      <Dialog
        open={pendingTeamDelete !== null}
        onClose={() => setPendingTeamDelete(null)}
      >
        <DialogTitle>{t('admin.teams.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingTeamDelete
              ? t('admin.teams.deleteDialog.body', { name: pendingTeamDelete.name })
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingTeamDelete(null)}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmTeamDelete()}
          >
            {t('admin.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingRemove !== null} onClose={() => setPendingRemove(null)}>
        <DialogTitle>{t('admin.teams.removeDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingRemove
              ? t('admin.teams.removeDialog.body', {
                  name: memberName(pendingRemove.membership),
                  team: pendingRemove.team.name,
                })
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemove(null)}>
            {t('admin.common.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => void confirmRemove()}>
            {t('admin.common.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
